import { Admin, Resource, Layout, CustomRoutes, AppBarProps } from 'react-admin';
import { dataProvider } from './dataProvider';
import UserIcon from '@mui/icons-material/Group';
import HandshakeIcon from '@mui/icons-material/Handshake';
import polyglotI18nProvider from 'ra-i18n-polyglot';
import { StaffCreate, StaffEdit, StaffList, StaffShow } from './resources/Accounts';
import { japaneseMessages } from './i18n/ja';
import authProvider from './authProvider';
import CustomMenu from './Menu';
import Dashboard from './resources/Dashboard';
import { SettingEdit } from './resources/Settings';
import {
  ProductRewardList,
  ProductRewardEdit,
  ProductRewardCreate,
  ProductRewardShow
} from './resources/settings/ProductRewardSettings';
import {
  UsageCountRewardRateCreate,
  UsageCountRewardRateEdit
} from './resources/settings/UsageCountRewardRateSettings';
import { InvitationCreate, InvitationList } from './resources/Invitations';
import { OrderList, OrderShow } from './resources/Orders';
import { BuyersList, BuyerOrderDetailPage } from './resources/Buyers';
import { AffiliatesList, AffiliateEdit } from './resources/Affiliates';
import { PartnerList, PartnerShow } from './resources/Partners';
import { InvoiceList, InvoiceShow } from './resources/Invoices';
import { PartnerPaymentList, PartnerPaymentDetailPage } from './resources/PartnerPayments';
import { InviteUrlShow } from './resources/InviteUrl';
import { ClientUrlEdit } from './resources/ClientUrl';
import CustomLoginPage from './common_modules/components/login/CustomLoginPage';
import Sales from './common_modules/components/Sales';
import theme from './theme';
import CreateStaff from './common_modules/components/CreateStaff';
import { ApiStatusProvider } from './ApiStatusContext';
import SMSAuthentication from './common_modules/components/SMSAuthentication';
import Completion from './common_modules/components/Completion';
import ClientRegister from './register/ClientRegister';
import ClientRegisterError from './register/ClientRegisterError';
import CustomAppBar from './common_modules/components/CustomAppBar';
import ForgotPasswordPage from './common_modules/components/login/ForgotPasswordPage';
import ResetPasswordSuccessPage from './common_modules/components/login/ResetPasswordSuccessPage';
import { Outlet, Route } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import ClientRegisterTokenError from './register/ClientRegisterTokenError';
import StartRegistration from './register/StartRegistration';
import Terms from './register/Terms';
import Contract from './register/Contract';
import Form from './register/Form';
import FormStore from './register/FormStore';
import FinishRegistration from './register/FinishRegistration';
import Verification from './register/Verification';
import Confirmation from './register/Confirmation';
import AuthHelper from './common_modules/utils/AuthHelper';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { jaJP } from '@mui/x-date-pickers/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import { NotificationList, NotificationShow } from './resources/Notifications';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { ContractSettingsEdit } from './resources/settings/ContractSettingsEdit';
import { TermsSettingsEdit } from './resources/settings/TermsSettingsEdit';
import { ContractPage } from './resources/Contracts';
import { CustomLayout } from './CustomLayout';
import { TermsAgreementProvider } from './common_modules/hooks/TermsAgreementContext';
import { TrackingCodePage } from './resources/TrackingCodePage';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import PageViewTracker from './PageViewTracker';
import { ShopInfoEdit } from './resources/settings/ShopInfoEdit';
import ShopIcon from '@mui/icons-material/Storefront';

AuthHelper.init(
  import.meta.env.VITE_COGNITO_ENDPOINT,
  import.meta.env.VITE_CLIENT_USER_POOL_ID,
  import.meta.env.VITE_CLIENT_USER_POOL_CLIENT_ID
);

dayjs.locale('ja');
const reCaptchaKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
export const i18nProvider = polyglotI18nProvider(() => japaneseMessages, 'ja');

const MyAppBar = (props: AppBarProps) => {
  return <CustomAppBar {...props} hasNotification={true} />;
};

const layout = (props: any) => (
  <ApiStatusProvider>
    <TermsAgreementProvider>
      <PageViewTracker />
      <CustomLayout {...props} appBar={MyAppBar} menu={CustomMenu} />
    </TermsAgreementProvider>
  </ApiStatusProvider>
);

const gtmParams = {
  id: import.meta.env.VITE_GTM_ID,
  nonce: 'zy9ekYQ4S4RjDOUd8ZcUFdKinTHPl0cWsr2fWQK5Ji0='
};

const RegisterFlowLayout = () => (
  <>
    <PageViewTracker />
    <Outlet />
  </>
);

export const App = () => (
  <LocalizationProvider
    dateAdapter={AdapterDayjs}
    adapterLocale="ja"
    localeText={jaJP.components.MuiLocalizationProvider.defaultProps.localeText}
  >
    <GTMProvider state={gtmParams}>
      <Admin
        layout={layout}
        dashboard={Dashboard}
        loginPage={() => <CustomLoginPage subtitle="for EC" backgroundColor="#78605C" buttonColor="#78605C" />}
        i18nProvider={i18nProvider}
        dataProvider={dataProvider}
        authProvider={authProvider}
        theme={theme}
        disableTelemetry={true}
      >
        <CustomRoutes noLayout>
          <Route
            path="/reset-password"
            element={<ForgotPasswordPage backgroundColor="#EDECED" buttonColor="#C59F98" />}
          />
          <Route
            path="/reset-complete"
            element={<ResetPasswordSuccessPage backgroundColor="#EDECED" buttonColor="#C59F98" />}
          />
        </CustomRoutes>

        <CustomRoutes noLayout>
          <Route element={<RegisterFlowLayout />}>
            <Route path="/register/client" element={<ClientRegister />} />
            <Route path="/register/client-staff" element={<ClientRegister />} />
            <Route path="/register/client/start-register" element={<StartRegistration />} />
            <Route path="/register/client/terms" element={<Terms />} />
            <Route path="/register/client/contract" element={<Contract />} />
            <Route path="/register/client/form" element={<Form />} />
            <Route path="/register/client/form-store" element={<FormStore />} />
            <Route
              path="/register/client/confirm"
              element={
                <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
                  <Confirmation />
                </GoogleReCaptchaProvider>
              }
            />
            {/* <Route path="/register/partner/upload-id" element={<UploadId />} /> */}
            <Route path="/register/client/finish" element={<FinishRegistration />} />

            <Route path="/register/client/create-staff" element={<CreateStaff />} />
            <Route path="/register/client/smsauth" element={<SMSAuthentication />} />
            <Route path="/register/client/complete" element={<Completion />} />
            <Route path="/register/client/verification" element={<Verification />} />

            <Route path="/register/client/error" element={<ClientRegisterError />} />
            <Route path="/register/client/token-error" element={<ClientRegisterTokenError />} />

            <Route path="/register/client-staff/create-staff" element={<CreateStaff />} />
            <Route path="/register/client-staff/smsauth" element={<SMSAuthentication />} />
            <Route path="/register/client-staff/complete" element={<Completion />} />
            <Route path="/register/client-staff/error" element={<ClientRegisterError />} />
          </Route>
        </CustomRoutes>

        <CustomRoutes>
          {/* 全体のアナリティクスは非表示 */}
          {/* <Route path="/all-sales" element={<Sales selectedResultType={1} isAdmin={false} />} /> */}{' '}
          <Route path="/sales" element={<Sales selectedResultType={3} isAdmin={false} />} />
          <Route path="/buyers/:fuid" element={<BuyerOrderDetailPage />} />
        </CustomRoutes>

        <Resource
          name="accounts"
          list={StaffList}
          show={StaffShow}
          edit={StaffEdit}
          create={StaffCreate}
          icon={UserIcon}
        />

        <Resource name="invitations" list={InvitationList} create={InvitationCreate} />

        <Resource name="affiliates" list={AffiliatesList} edit={AffiliateEdit} icon={HandshakeIcon} />

        <Resource name="orders" list={OrderList} show={OrderShow} icon={UserIcon} />

        <Resource name="buyers" list={BuyersList} icon={UserIcon} />

        <Resource name="invoices" list={InvoiceList} show={InvoiceShow} icon={UserIcon} />

        <CustomRoutes>
          <Route path="/partner-payments" element={<PartnerPaymentList />} />
          <Route path="/partner-payments/:id" element={<PartnerPaymentDetailPage />} />
        </CustomRoutes>

        <Resource name="settings" edit={SettingEdit} icon={UserIcon} />

        <Resource
          name="product-reward-settings"
          list={ProductRewardList}
          edit={ProductRewardEdit}
          create={ProductRewardCreate}
          show={ProductRewardShow}
        />

        <Resource name="contract-settings" edit={ContractSettingsEdit} />
        {/* <Resource name="terms-settings" edit={TermsSettingsEdit} /> */}

        <CustomRoutes>
          <Route path="/api-settings" element={<TrackingCodePage />} />
        </CustomRoutes>

        <Resource name="terms-settings" list={TermsSettingsEdit} />

        <Resource name="invite-url" show={InviteUrlShow} />

        <Resource name="client-url" show={ClientUrlEdit} />

        <CustomRoutes>
          <Route path="/contracts" element={<ContractPage />} />
        </CustomRoutes>
        <CustomRoutes>
          <Route path="/product-reward-settings/create-exclusion" element={<ProductRewardCreate isExclude={true} />} />
        </CustomRoutes>

        <Resource
          name="product-reward-settings"
          edit={ProductRewardEdit}
          create={ProductRewardCreate}
          show={ProductRewardShow}
        />

        <Resource name="client-pr" edit={ShopInfoEdit} icon={ShopIcon} />

        <Resource name="client-pr-images" />

        <Resource
          name="usage-count-reward-rates"
          create={UsageCountRewardRateCreate}
          edit={UsageCountRewardRateEdit}
        />

      </Admin>
    </GTMProvider>
  </LocalizationProvider>
);
